/**
 * Google reCAPTCHA v3 Integration Component
 * 
 * Automaticky integruje Google reCAPTCHA v3 do formulářů
 * Inspirováno implementací na https://moje.spp.cz/registrace.html
 * 
 * @author MerlinCMS
 */

// 💾 Variables
let recaptchaSiteKey = null;
let recaptchaLoaded = false;

// 📐 Functions

/**
 * Inicializuje reCAPTCHA v3
 * @param {string} siteKey - Google reCAPTCHA Site Key
 */
const initRecaptcha = (siteKey) => {
  if (!siteKey) {
    console.error('reCAPTCHA v3: Site Key není definován');
    return;
  }
  
  recaptchaSiteKey = siteKey;
  
  // Načtení reCAPTCHA skriptu pokud ještě není načten
  if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    
    script.onload = () => {
      recaptchaLoaded = true;
      console.log('reCAPTCHA v3 script loaded');
    };
    
    script.onerror = () => {
      console.error('Nepodařilo se načíst reCAPTCHA v3 script');
    };
  } else {
    recaptchaLoaded = true;
  }
};

/**
 * Získá reCAPTCHA token pro danou akci
 * @param {string} action - Akce pro kterou se token generuje (např. 'submit', 'contact', 'login')
 * @returns {Promise<string>} - Promise s tokenem
 */
const getRecaptchaToken = (action = 'submit') => {
  return new Promise((resolve, reject) => {
    if (!recaptchaSiteKey) {
      reject(new Error('reCAPTCHA není inicializována'));
      return;
    }
    
    if (!recaptchaLoaded || typeof grecaptcha === 'undefined') {
      reject(new Error('reCAPTCHA script není načten'));
      return;
    }
    
    grecaptcha.ready(() => {
      grecaptcha.execute(recaptchaSiteKey, { action: action })
        .then((token) => {
          resolve(token);
        })
        .catch((error) => {
          reject(error);
        });
    });
  });
};

/**
 * Přidá reCAPTCHA token do formuláře
 * @param {HTMLFormElement} form - Formulář
 * @param {string} action - Akce pro reCAPTCHA
 * @returns {Promise<void>}
 */
const addRecaptchaTokenToForm = async (form, action = 'submit') => {
  try {
    const token = await getRecaptchaToken(action);
    
    // Najdeme nebo vytvoříme hidden input pro token
    let tokenInput = form.querySelector('input[name="recaptcha-token"]');
    if (!tokenInput) {
      tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'recaptcha-token';
      tokenInput.id = 'recaptcha-token';
      form.appendChild(tokenInput);
    }
    
    tokenInput.value = token;
    console.log('reCAPTCHA token přidán do formuláře:', action);
    
    return token;
  } catch (error) {
    console.error('Chyba při získávání reCAPTCHA tokenu:', error);
    throw error;
  }
};

/**
 * Automaticky připojí reCAPTCHA k formulářům s atributem data-recaptcha
 */
const autoAttachRecaptcha = () => {
  const forms = document.querySelectorAll('form[data-recaptcha]');
  
  forms.forEach(form => {
    const action = form.dataset.recaptcha || 'submit';
    
    // Přidáme event listener pro submit
    form.addEventListener('submit', async (e) => {
      // Pokud už má token, nebudeme ho znovu generovat
      const existingToken = form.querySelector('input[name="recaptcha-token"]');
      if (existingToken && existingToken.value) {
        return;
      }
      
      e.preventDefault();
      
      try {
        await addRecaptchaTokenToForm(form, action);
        // Po přidání tokenu znovu odešleme formulář
        form.submit();
      } catch (error) {
        console.error('reCAPTCHA chyba při odesílání formuláře:', error);
        // Můžeme zobrazit chybovou zprávu uživateli
        alert('Došlo k chybě při ověřování. Zkuste to prosím znovu.');
      }
    });
  });
};

/**
 * Integrace s MerlinCMS form-send systémem
 * Automaticky přidá reCAPTCHA token před odesláním formuláře
 */
const integrateWithFormSend = () => {
  // Posloucháme na custom event před odesláním formuláře
  document.addEventListener('beforeFormSend', async (e) => {
    const form = e.detail.form;
    const action = form.dataset.recaptcha;
    
    if (action) {
      try {
        await addRecaptchaTokenToForm(form, action);
      } catch (error) {
        console.error('reCAPTCHA chyba v form-send:', error);
        e.preventDefault(); // Zastavíme odesílání formuláře
      }
    }
  });
};

/**
 * Inicializace komponenty
 */
const initRecaptchaComponent = () => {
  // Pokusíme se najít site key v globálních proměnných nebo data atributech
  let siteKey = null;
  
  // 1. Zkusíme globální proměnnou
  if (window.recaptchaSiteKey) {
    siteKey = window.recaptchaSiteKey;
  }
  
  // 2. Zkusíme najít v meta tagu
  const metaRecaptcha = document.querySelector('meta[name="recaptcha-site-key"]');
  if (metaRecaptcha) {
    siteKey = metaRecaptcha.content;
  }
  
  // 3. Zkusíme najít v data atributu na body
  if (document.body.dataset.recaptchaSiteKey) {
    siteKey = document.body.dataset.recaptchaSiteKey;
  }
  
  if (siteKey) {
    initRecaptcha(siteKey);
    autoAttachRecaptcha();
    integrateWithFormSend();
    
    console.log('reCAPTCHA v3 komponenta inicializována');
  } else {
    // console.warn('reCAPTCHA v3: Site Key nebyl nalezen. Komponenta nebude aktivní.');
  }
};

// 🖱️ Event Listeners

// Inicializace po načtení DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecaptchaComponent);
} else {
  initRecaptchaComponent();
}

// 📤 Export
export { 
  initRecaptcha, 
  getRecaptchaToken, 
  addRecaptchaTokenToForm,
  autoAttachRecaptcha 
};
